import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { PageBackground } from '@/components/ui/page-background';
import { PageHero } from '@/components/ui/page-hero';
import { ArrowRight, Clock, TrendingUp, Users, Palette, Smartphone, Bot, Code, ShoppingBag, Search } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const services = [
  {
    name: "Website Development",
    slug: "website-development",
    pitch: "Fast, conversion-focused websites that grow your business",
    benefits: [
      "47% average conversion lift",
      "90+ Core Web Vitals scores",
      "2-4 week launch timeline"
    ],
    timeline: "2-4 weeks",
    icon: TrendingUp,
    accent: "from-cyan-500/20 to-blue-500/20"
  },
  {
    name: "UI/UX Design",
    slug: "ui-ux-design",
    pitch: "User experiences that feel intuitive and drive engagement",
    benefits: [
      "67% improvement in user retention",
      "42% faster task completion",
      "Research-backed design decisions"
    ],
    timeline: "3-5 weeks",
    icon: Palette,
    accent: "from-purple-500/20 to-pink-500/20"
  },
  {
    name: "Web App Development",
    slug: "web-app-development",
    pitch: "Scalable web applications built for growth and performance",
    benefits: [
      "99.9% uptime reliability",
      "50% faster load times",
      "Enterprise-grade security"
    ],
    timeline: "6-12 weeks",
    icon: Code,
    accent: "from-green-500/20 to-emerald-500/20"
  },
  {
    name: "AI Integration",
    slug: "ai-integration",
    pitch: "Smart automation that reduces costs and improves outcomes",
    benefits: [
      "60% reduction in manual tasks",
      "35% improvement in accuracy",
      "ROI visible within 90 days"
    ],
    timeline: "4-8 weeks",
    icon: Bot,
    accent: "from-orange-500/20 to-amber-500/20"
  },
  {
    name: "Custom Software",
    slug: "custom-software",
    pitch: "Tailored solutions that solve your specific business challenges",
    benefits: [
      "100% fit to your workflow",
      "3x faster than off-the-shelf",
      "Full ownership and control"
    ],
    timeline: "8-16 weeks",
    icon: Users,
    accent: "from-red-500/20 to-rose-500/20"
  },
  {
    name: "Mobile App Development",
    slug: "mobile-app-development",
    pitch: "Native and cross-platform apps users love to use",
    benefits: [
      "4.8+ app store ratings",
      "85% user retention at 30 days",
      "iOS and Android deployment"
    ],
    timeline: "8-14 weeks",
    icon: Smartphone,
    accent: "from-indigo-500/20 to-violet-500/20"
  },
  {
    name: "Ecommerce",
    slug: "ecommerce",
    pitch: "Online stores optimized for sales and customer experience",
    benefits: [
      "32% increase in conversion rates",
      "45% boost in average order value",
      "Seamless checkout experience"
    ],
    timeline: "4-8 weeks",
    icon: ShoppingBag,
    accent: "from-teal-500/20 to-cyan-500/20"
  },
  {
    name: "SEO Services",
    slug: "seo-services",
    pitch: "Rank higher and attract more qualified organic traffic",
    benefits: [
      "150% increase in organic traffic",
      "Top 3 rankings for target keywords",
      "Technical SEO optimization"
    ],
    timeline: "3-6 months",
    icon: Search,
    accent: "from-yellow-500/20 to-orange-500/20"
  }
];

const Services = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.map((service, index) => ({
      "@type": "Service",
      "position": index + 1,
      "name": service.name,
      "description": service.pitch,
      "url": `https://www.affinityaim.com/services/${service.slug}`
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.affinityaim.com/services"
      }
    ]
  };

  return (
    <PageBackground>
      <SEO
        title="Our Services - Web Development, UI UX Design & AI Integration | AffinityAim"
        description="Explore our premium digital services: website development, UI/UX design, web apps, mobile apps, AI integration, and SEO. Proven results for growth-stage companies."
        canonical="https://www.affinityaim.com/services"
        keywords="web development services, UI UX design, web app development, mobile app development, AI integration, SEO services"
        schema={[servicesSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        <PageHero
          label="Services"
          title="We build digital products"
          titleAccent="that deliver results"
          description="From websites to web apps, our senior team ships fast and measures what matters. Clear timelines, transparent process, proven outcomes."
        />

        {/* Services Grid */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div ref={elementRef} className="max-w-6xl mx-auto">
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Link 
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="group tap-target"
                      style={getRevealStyle(index)}
                      aria-label={`Learn more about ${service.name}`}
                    >
                      <div className="h-full p-8 rounded-2xl bg-surface/50 border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5 relative overflow-hidden">
                        {/* Accent gradient */}
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${service.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                        
                        {/* Header */}
                        <div className="mb-6">
                          <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                              <IconComponent className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              {service.timeline}
                            </div>
                          </div>
                          
                          <h3 className="font-display font-semibold text-foreground mb-3 text-xl group-hover:text-primary transition-colors duration-300">
                            {service.name}
                          </h3>
                          
                          <p className="text-muted-foreground leading-relaxed">
                            {service.pitch}
                          </p>
                        </div>

                        {/* Benefits */}
                        <div className="mb-6">
                          <div className="space-y-3">
                            {service.benefits.map((benefit, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                                <span className="text-sm text-muted-foreground">{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="flex items-center justify-between pt-4 border-t border-border/50">
                          <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">Learn more</span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-2 transition-all duration-300" />
                        </div>

                      </div>
                    </Link>
                  );
                })}
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
          
          <div className="container mx-auto px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              
              <h2 className="font-display font-bold text-foreground mb-6" style={{ fontSize: 'clamp(32px, 5vw, 48px)', lineHeight: '1.1' }}>
                Not sure which service you need?
              </h2>
              <p className="text-muted-foreground mb-10 text-lg max-w-xl mx-auto">
                Book a 15-minute call and we'll help you choose the right approach for your goals and timeline.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-base">
                    Book a strategy call
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" size="lg" className="h-14 px-8 text-base border-border hover:bg-surface hover:border-primary/30">
                    View our work
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default Services;
